import React from "react";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
} from "@mui/material";

export const MetricCardSkeleton: React.FC = () => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
          : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
      border: (theme) =>
        theme.palette.mode === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
      borderRadius: 3,
    }}
  >
    <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
        <Skeleton
          variant="rectangular"
          width={40}
          height={40}
          sx={{ borderRadius: 2 }}
        />
      </Box>
      
      <Skeleton
        variant="text"
        width="60%"
        height={40}
        sx={{ mb: 1 }}
      />
      
      <Skeleton
        variant="text"
        width="80%"
        height={20}
        sx={{ mb: 1 }}
      />
      
      <Skeleton
        variant="text"
        width="70%"
        height={16}
      />
    </CardContent>
  </Card>
);

export const HoldingsListSkeleton: React.FC = () => (
  <Card
    elevation={0}
    sx={{
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
          : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
      border: (theme) =>
        theme.palette.mode === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
      borderRadius: 3,
      mb: 4,
    }}
  >
    <CardContent sx={{ p: 4 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Skeleton
          variant="rectangular"
          width={40}
          height={40}
          sx={{ borderRadius: 2 }}
        />
        <Box>
          <Skeleton variant="text" width={200} height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={150} height={16} />
        </Box>
      </Box>

      <Stack spacing={3}>
        {[1, 2, 3].map((item) => (
          <Box key={item}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                borderRadius: 2,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                />
                <Box>
                  <Skeleton variant="text" width={60} height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={120} height={16} />
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={4}>
                <Box textAlign="right">
                  <Skeleton variant="text" width={60} height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={50} height={16} />
                </Box>
                
                <Box textAlign="right">
                  <Skeleton variant="text" width={50} height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={40} height={16} />
                </Box>
                
                <Box textAlign="right">
                  <Skeleton variant="text" width={50} height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={60} height={16} />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>
    </CardContent>
  </Card>
);

export const PaymentHistorySkeleton: React.FC = () => (
  <Card
    elevation={0}
    sx={{
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
          : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
      border: (theme) =>
        theme.palette.mode === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
      borderRadius: 3,
      mb: 4,
    }}
  >
    <CardContent sx={{ p: 4 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Skeleton
          variant="rectangular"
          width={40}
          height={40}
          sx={{ borderRadius: 2 }}
        />
        <Box>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={140} height={16} />
        </Box>
      </Box>

      <Stack spacing={2}>
        {[1, 2, 3, 4, 5].map((item) => (
          <Box key={item}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                borderRadius: 2,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
              }}
            >
              <Box display="flex" alignItems="center" gap={3}>
                <Skeleton
                  variant="circular"
                  width={36}
                  height={36}
                />
                <Box>
                  <Skeleton variant="text" width={50} height={18} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={100} height={14} />
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={4}>
                <Box textAlign="right">
                  <Skeleton variant="text" width={60} height={18} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={40} height={14} />
                </Box>
                
                <Box textAlign="right">
                  <Skeleton variant="text" width={80} height={18} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={70} height={14} />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>
    </CardContent>
  </Card>
);

export const GraphSkeleton: React.FC = () => (
  <Card
    elevation={0}
    sx={{
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
          : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
      border: (theme) =>
        theme.palette.mode === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
      borderRadius: 3,
      mb: 4,
    }}
  >
    <CardContent sx={{ p: 4 }}>
      <Box mb={3}>
        <Skeleton variant="text" width={250} height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={300} height={20} />
      </Box>

      <Box sx={{ height: 400, display: "flex", alignItems: "end", gap: 2, px: 4 }}>
        {[1, 2, 3, 4, 5].map((item) => (
          <Skeleton
            key={item}
            variant="rectangular"
            sx={{
              width: "100%",
              height: `${60 + Math.random() * 200}px`,
              borderRadius: 1,
            }}
          />
        ))}
      </Box>

      <Box
        display="flex"
        justifyContent="center"
        gap={4}
        mt={3}
        pt={3}
        sx={{
          borderTop: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.1)",
        }}
      >
        {[1, 2, 3].map((item) => (
          <Box key={item} textAlign="center">
            <Skeleton variant="text" width={80} height={28} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width={60} height={16} />
          </Box>
        ))}
      </Box>
    </CardContent>
  </Card>
);